import { useFormik } from "formik";
import { ArrowRight, Check, CheckCircle2, Circle, Eye, EyeOff, Lock, ShieldCheck } from "lucide-react";
import React, { useState } from "react";
import { Link, useParams } from "react-router-dom";
import * as yup from "yup";
import AuthLayout from "@/components/Layout/AuthLayout";
import api from "@/utils/api";

export const RecoveryNewPassword = () => {
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [visibleFields, setVisibleFields] = useState({});
  const { hash } = useParams();

  const form = useFormik({
    initialValues: { newPassword: "", passwordConfirmation: "" },
    validationSchema: yup.object({
      newPassword: yup.string().min(8, "A nova senha deve conter no mínimo 8 caracteres.").required("Nova senha é obrigatória!"),
      passwordConfirmation: yup.string().required("Confirmação da nova senha é obrigatória!").oneOf([yup.ref("newPassword")], "As novas senhas devem ser iguais."),
    }),
    onSubmit: async values => {
      setLoading(true);
      setErrorMessage("");
      try {
        await api.patch(`/partners/recover_password/${hash}`, {
          data: { attributes: { password: values.newPassword, password_confirmation: values.passwordConfirmation } },
        });
        form.resetForm();
        setSuccess(true);
      } catch (error) {
        setErrorMessage("Não foi possível alterar a senha. Confira o link recebido ou solicite uma nova recuperação.");
      } finally {
        setLoading(false);
      }
    },
  });

  const password = form.values.newPassword;
  const rules = [
    { label: "Pelo menos 8 caracteres", valid: password.length >= 8 },
    { label: "Uma letra maiúscula e uma minúscula", valid: /[A-Z]/.test(password) && /[a-z]/.test(password) },
    { label: "Um número ou símbolo", valid: /[0-9]|[^a-zA-Z0-9]/.test(password) },
    { label: "As duas senhas coincidem", valid: password.length > 0 && password === form.values.passwordConfirmation },
  ];
  const strength = rules.slice(0, 3).filter(rule => rule.valid).length;
  const strengthLabel = strength === 3 ? "Senha forte" : strength === 2 ? "Senha média" : "Senha fraca";

  return (
    <AuthLayout action={success ? <><span>Precisa de ajuda?</span><a href="mailto:contato@sacgpt.com.br">Falar com suporte</a></> : <><span>Lembrou a senha?</span><Link to="/">Entrar</Link></>}>
      {success ? (
        <>
          <div className="partner-auth-status-icon"><ShieldCheck /></div>
          <h1>Senha alterada</h1>
          <p className="partner-auth-description">Sua nova senha já está valendo. Entre na sua conta para continuar.</p>
          <Link to="/" className="partner-auth-submit"><ArrowRight />Entrar na minha conta</Link>
        </>
      ) : (
        <>
          <h1>Criar nova senha</h1>
          <p className="partner-auth-description">Escolha uma senha forte para sua conta.</p>
          <form onSubmit={form.handleSubmit} noValidate>
            <div className="partner-auth-fields">
              {[{ id: 'newPassword', label: 'Nova senha' }, { id: 'passwordConfirmation', label: 'Confirmar nova senha' }].map(field => (
                <div className="partner-auth-field" key={field.id}>
                  <label htmlFor={field.id}>{field.label}</label>
                  <div className="partner-auth-input">
                    <Lock aria-hidden="true" />
                    <input id={field.id} name={field.id} type={visibleFields[field.id] ? 'text' : 'password'} autoComplete="new-password" placeholder="••••••••" value={form.values[field.id]} onChange={form.handleChange} onBlur={form.handleBlur} aria-invalid={!!(form.touched[field.id] && form.errors[field.id])} aria-describedby={form.touched[field.id] && form.errors[field.id] ? `${field.id}-error` : undefined} />
                    <button type="button" aria-label={`${visibleFields[field.id] ? 'Ocultar' : 'Mostrar'} ${field.label.toLowerCase()}`} onClick={() => setVisibleFields(fields => ({ ...fields, [field.id]: !fields[field.id] }))}>{visibleFields[field.id] ? <Eye /> : <EyeOff />}</button>
                  </div>
                  {field.id === 'newPassword' && password && <><div className="partner-auth-strength" aria-label={strengthLabel}>{[1, 2, 3, 4].map(level => <span key={level} data-active={strength === 3 || strength >= level} />)}</div><p className="partner-auth-strength-label">{strengthLabel}</p></>}
                  {form.touched[field.id] && form.errors[field.id] && <p id={`${field.id}-error`} className="partner-auth-error">{form.errors[field.id]}</p>}
                </div>
              ))}
            </div>
            <ul className="partner-auth-password-rules">{rules.map(rule => <li key={rule.label} data-valid={rule.valid}>{rule.valid ? <CheckCircle2 size={14} /> : <Circle size={14} />}{rule.label}</li>)}</ul>
            {errorMessage && <p role="alert" className="partner-auth-error">{errorMessage}</p>}
            <button type="submit" className="partner-auth-submit" disabled={loading}><Check />{loading ? 'Salvando...' : 'Salvar nova senha'}</button>
          </form>
        </>
      )}
    </AuthLayout>
  );
};

export default RecoveryNewPassword;
